import { Component, OnInit } from '@angular/core';
import { LogService } from '../../../services/log.service';

interface Message {
  id: number;
  sender: 'user' | 'bot';
  text: string;
  timestamp: Date;
  sentiment: string;
  sentimentScore: number;
  keywords?: string[];
  escalated?: boolean;
}

@Component({
  selector: 'app-conversation-log',
  templateUrl: './conversation-log.component.html',
  styleUrls: ['./conversation-log.component.css']
})
export class ConversationLogComponent implements OnInit {
  conversations: any[] = [];
  selectedConvo: any = null;
  messages: Message[] = [];
  loading = false;
  messagesLoading = false;
  selectedMessageId: number | null = null;

  constructor(private logService: LogService) { }

  ngOnInit() {
    this.loadConversations();
  }

  loadConversations() {
    this.loading = true;
    this.logService.getConversations().subscribe({
      next: (res) => {
        this.loading = false;
        this.conversations = res.conversations || [];
      },
      error: () => this.loading = false
    });
  }

  selectConversation(convo: any) {
    this.selectedConvo = convo;
    this.messagesLoading = true;
    this.selectedMessageId = null;
    this.logService.getMessages(convo.id).subscribe({
      next: (res) => {
        this.messagesLoading = false;
        if (res.success) {
          this.messages = res.messages.map((m: any) => ({
            id: m.id,
            sender: m.sender_type,
            text: m.message_text,
            timestamp: new Date(m.created_at || m.timestamp || Date.now()),
            sentiment: m.sentiment || 'neutral',
            sentimentScore: 0.5, // Placeholder if not explicitly stored as number
            keywords: m.intent ? [m.intent] : []
          }));
        }
      },
      error: () => this.messagesLoading = false
    });
  }

  selectMessage(messageId: number) {
    this.selectedMessageId = this.selectedMessageId === messageId ? null : messageId;
  }

  getSentimentColor(sentiment: string): string {
    const s = sentiment.toLowerCase();
    if (s.includes('positive')) return 'text-emerald-600';
    if (s.includes('negative')) return 'text-orange-600';
    if (s.includes('frustrated')) return 'text-red-600';
    return 'text-blue-600';
  }

  getSentimentBgColor(sentiment: string): string {
    const s = sentiment.toLowerCase();
    if (s.includes('positive')) return 'bg-emerald-100';
    if (s.includes('negative')) return 'bg-orange-100';
    if (s.includes('frustrated')) return 'bg-red-100';
    return 'bg-blue-100';
  }

  getSentimentIcon(sentiment: string): string {
    const s = sentiment.toLowerCase();
    if (s.includes('positive')) return '😊';
    if (s.includes('negative')) return '😟';
    if (s.includes('frustrated')) return '😤';
    return '😐';
  }

  getStatusColor(status: string): string {
    if (!status) return 'bg-slate-500';
    const s = status.toLowerCase();
    if (s === 'active') return 'bg-blue-500';
    if (s === 'resolved') return 'bg-emerald-500';
    if (s === 'escalated') return 'bg-orange-500';
    return 'bg-slate-500';
  }

  exportConversation() {
    if (!this.selectedConvo) return;
    const data = JSON.stringify({ convo: this.selectedConvo, messages: this.messages }, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `conversation-${this.selectedConvo.session_id}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  calculateDuration(): string {
    if (!this.messages.length) return 'N/A';
    const start = this.messages[0].timestamp.getTime();
    const end = this.messages[this.messages.length - 1].timestamp.getTime();
    const diff = end - start;
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  }
}
